import {
    executeAwaitableHandlerChain,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import { batchGetProducts, BatchGetProductsApiParameter } from "@insite/client-framework/Services/ProductService";
import { StorefrontAccess } from "@insite/client-framework/Services/SettingsService";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";

import {
    BatchLoadProductsResult,
    chain,
    DispatchBeginBatchLoadProducts,
    LoadRealTimeInventory,
    ProcessProducts,
    RequestDataFromApi,
    UploadError,
} from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import {
    OrderUploadRowError,
    UploadedItem,
} from "@insite/client-framework/Store/Components/OrderUpload/OrderUploadState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { ProductDto,  RealTimeInventoryModel, } from "@insite/client-framework/Types/ApiModels";
/* ---------------------------------- Types --------------------------------- */

type batchLoadProductsProps = Parameters<typeof DispatchBeginBatchLoadProducts>[0];
type ProductWithProperties = ProductDto & {
    properties?: Record<string, string>;
};

// export interface CustomProductDto extends ProductDto {
//     properties?: Record<string, string>;
// }

// export interface BatchLoadProductsResult {
//     apiParameter: BatchGetProductsApiParameter;
//     apiResult: (CustomProductDto | null)[];
//     products: ProductDto[];
//     rowErrors: OrderUploadRowError[];
// }


/* ------------------------------ Handler Steps ------------------------------ */

async function CustomRequestDataFromApi(props: batchLoadProductsProps) {
    try {
        props.result.apiResult =
            (await batchGetProducts(
                props.result.apiParameter,
            )) as (ProductWithProperties | null)[];
    } catch (error: any) {
        const { storefrontAccess } =
            getSettingsCollection(props.getState()).productSettings;

        if (
            storefrontAccess === StorefrontAccess.SignInRequiredToBrowse &&
            error?.status === 403
        ) {
            error.status = 401;
            error.errorJson = { message: "error" };
        }

        throw error;
    }
}

async function CustomLoadRealTimeInventory(props: batchLoadProductsProps) : Promise<void>{
    if (!props.parameter.checkInventory) {
        return;
    }

    const products = props.result.apiResult.filter(p => !!p) as ProductDto[];
    if (!products.length) {
        return;
    }

    const realTimeInventory = await executeAwaitableHandlerChain<
        Parameters<typeof loadRealTimeInventory>[0],
        RealTimeInventoryModel
    >(
        loadRealTimeInventory,
        {
            productIds: products.map(o => o.id),
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps.error) {
                    props.dispatch({
                        type: "Components/OrderUpload/SetFailedToLoadInventory",
                        parameter: { failedToLoadInventory: true },
                    });
                }
            },
        },
        {
            dispatch: props.dispatch,
            getState: props.getState,
        },
    );

    realTimeInventory.realTimeInventoryResults?.forEach(inventory => {
        products
            .filter(p => p.id === inventory.productId)
            .forEach(product  => {
                product.qtyOnHand = inventory.qtyOnHand;

                const inventoryAvailability = inventory.inventoryAvailabilityDtos?.find(
                    o => o.unitOfMeasure === product.unitOfMeasure,
                );
                product.availability = inventoryAvailability?.availability || {
                    messageType: 0,
                    message: "",
                    requiresRealTimeInventory: false,
                };

                product.productUnitOfMeasures?.forEach(productUoM => {
                    const inventoryAvailability = inventory.inventoryAvailabilityDtos?.find(
                        o => o.unitOfMeasure === productUoM.unitOfMeasure,
                    );
                    productUoM.availability = inventoryAvailability?.availability || {
                        messageType: 0,
                        message: "",
                        requiresRealTimeInventory: false,
                    };
                });

                const additionalResults = inventory?.additionalResults;
                const inventoryBackOrder =
                    !!additionalResults && "backOrder" in additionalResults;

                const p = ensureProperties(product);
                if(p.properties){
                    p.properties.backOrder = inventoryBackOrder != null ? String(inventoryBackOrder) : "false";
                }
            });
    });
}

function CustomProcessProducts(props: batchLoadProductsProps) {
    const state = props.getState();
    const processState = resolveProcessState(state);

    if ("uploadCancelled" in processState && processState.uploadCancelled) {
        return false;
    }

    props.result.apiResult.forEach((product, i) => {
        const item = getUploadedItem(processState, i);
        if (!item) {
            return;
        }

        const rowIndex = props.parameter.firstRowHeading ? i + 2 : i + 1;

        if (!product) {
            props.result.rowErrors.push(
                mapRowError(rowIndex, UploadError.NotFound, item.name, {
                    qtyOrdered: item.qtyOrdered,
                    unitOfMeasureDisplay: item.unitOfMeasure,
                } as ProductDto),
            );
            return;
        }

        const error = validateProduct(product, props.parameter.checkInventory);
        if (error !== UploadError.None) {
            product.qtyOrdered = item.qtyOrdered ?? 0;
            props.result.rowErrors.push(
                mapRowError(rowIndex, error, item.name, product),
            );
            return;
        }

        product.qtyOrdered ||= 1;

        if (
            setProductUnitOfMeasure(
                product,
                item,
                rowIndex,
                props.result.rowErrors,
            )
        ) {
            return;
        }

        addProductToList(
            product,
            item,
            rowIndex,
            props.result,
            props.parameter.checkInventory,
        );
    });
}

/* ------------------------------- Chain Setup ------------------------------- */

replaceInChain(chain,RequestDataFromApi,CustomRequestDataFromApi)
replaceInChain(chain,LoadRealTimeInventory,CustomLoadRealTimeInventory)
replaceInChain(chain, ProcessProducts, CustomProcessProducts);
/* ------------------------------- Helpers ----------------------------------- */

const resolveProcessState = (state: any) => {
    const page = getCurrentPage(state);
    const isVmi =
        page &&
        ["VmiBinsPage", "VmiLocationDetailsPage"].includes(page.type);

    if (!isVmi) {
        return state.components.orderUpload;
    }

    return state.components.vmiBinsImportModal.modalIsOpen
        ? state.components.vmiBinsImportModal
        : state.components.vmiCountsImportModal;
};

const getUploadedItem = (state: any, index: number): UploadedItem | null =>
    state.uploadedItems?.[index] ?? state.parsedItems?.[index] ?? null;

const hasTrueProperty = (
    product: ProductDto & { properties?: Record<string, string> },
    key: string,
) => product.properties?.[key]?.toLowerCase() === "true";

const validateProduct = (
    product: ProductDto & { properties?: Record<string, string> },
    checkInventory: boolean,
): UploadError => {
    if (product.canConfigure || (product.isConfigured && !product.isFixedConfiguration)) {
        return UploadError.ConfigurableProduct;
    }

    if (product.isStyleProductParent) {
        return UploadError.StyledProduct;
    }

    if (
        checkInventory &&
        product.trackInventory &&
        !product.canBackOrder &&
        product.qtyOnHand === 0
    ) {
        return UploadError.OutOfStock;
    }

    if (!product.canAddToCart) {
        return UploadError.Unavailable;
    }

    if ((!product.canAddToCart && !hasTrueProperty(product, "backOrder")) || hasTrueProperty(product, "priceNotMaintained") ) {
        return UploadError.WrongType;
    }

    return UploadError.None;
};

const setProductUnitOfMeasure = (
    product: ProductDto,
    item: UploadedItem,
    index: number,
    rowErrors: OrderUploadRowError[],
): boolean => {
    const uom = findMatchingUom(product, item);
    if (uom) {
        Object.assign(product, {
            unitOfMeasure: uom.unitOfMeasure,
            unitOfMeasureDisplay: uom.unitOfMeasureDisplay,
            selectedUnitOfMeasure: uom.unitOfMeasure,
            selectedUnitOfMeasureDisplay: uom.unitOfMeasureDisplay,
        });
        return false;
    }

    if (item.unitOfMeasure) {
        const error = mapRowError(
            index,
            UploadError.InvalidUnit,
            item.name,
            product,
        );
        error.umRequested = item.unitOfMeasure;
        rowErrors.push(error);
        return true;
    }

    return false;
};

const addProductToList = (
    product: ProductDto  & { properties?: Record<string, string>},
    item: UploadedItem,
    index: number,
    result: BatchLoadProductsResult,
    checkInventory: boolean,
) => {
    if (
        checkInventory &&
        product.trackInventory &&
        !product.canBackOrder &&
        !product.quoteRequired &&
        exceedsInventory(product)
    ) {
        result.rowErrors.push(
            mapRowError(index, UploadError.NotEnough, item.name, product),
        );
    }

    result.products.push(product);
};

const exceedsInventory = (product: ProductDto) => {
    const base = getBaseUom(product);
    const current = getCurrentUom(product);

    return (
        product.qtyOrdered * base.qtyPerBaseUnitOfMeasure >
        product.qtyOnHand * current.qtyPerBaseUnitOfMeasure
    );
};

const getBaseUom = (product: ProductDto) =>
    product.productUnitOfMeasures?.find(u => u.isDefault) ??
    getDefaultUom();

const getCurrentUom = (product: ProductDto) =>
    product.productUnitOfMeasures?.find(
        u => u.unitOfMeasure === product.unitOfMeasure,
    ) ?? getDefaultUom();

const getDefaultUom = () => ({
    productUnitOfMeasureId: "",
    unitOfMeasure: "",
    unitOfMeasureDisplay: "",
    description: "",
    qtyPerBaseUnitOfMeasure: 1,
    roundingRule: "",
    isDefault: false,
    availability: null,
});

const mapRowError = (
    index: number,
    error: UploadError,
    name: string,
    product: ProductDto,
): OrderUploadRowError => ({
    index,
    error,
    name,
    qtyRequested: product.qtyOrdered,
    umRequested: product.unitOfMeasureDisplay,
    qtyOnHand: product.qtyOnHand,
});

const findMatchingUom = (product: ProductDto, item: UploadedItem) => {
    if (!item.unitOfMeasure) {
        return null;
    }

    const value = item.unitOfMeasure.toLowerCase();

    return product.productUnitOfMeasures?.find(
        u =>
            u.unitOfMeasure.toLowerCase() === value ||
            u.unitOfMeasureDisplay.toLowerCase() === value ||
            u.description.toLowerCase() === value,
    );
};

const ensureProperties = (product: ProductDto): ProductWithProperties => {
    const p = product as ProductWithProperties;
    p.properties ??= {};
    return p;
};
